/**
 * Lightweight, process-local diagnostics sink for the visualization cube caches.
 *
 * The cube path has two bounded caches — the IndexedDB byte cache (`CubeCache`,
 * byte budget) and the in-memory decoded-table registry (`CubeTableRegistry`,
 * `MAX_RESIDENT_TABLES`) — plus a per-origin IndexedDB quota. None of those limits are visible
 * in the UI today, so when testing against a large dataset you can silently hit an eviction or
 * a quota error and not realise the workspace has started round-tripping every interaction.
 *
 * This module records those events (and successful loads) into a small ring buffer with running
 * counters, and lets the admin diagnostics widget subscribe. It deliberately lives OUTSIDE Redux
 * for the same reason `CubeTableRegistry` does — these are infra signals, not view state, and we
 * don't want eviction churn triggering store updates.
 *
 * Every recorded event also emits a `console.warn` for the cap/quota kinds, so the limits are
 * noticeable even without the widget open.
 *
 */

export type CubeDiagEventKind =
  | 'loaded' // a cube finished loading (fresh or 304) and was registered
  | 'idb-evict' // CubeCache evicted an LRU entry to stay within its byte budget / MAX_ENTRIES
  | 'idb-quota' // CubeCache hit a quota/write error persisting a cube
  | 'idb-error' // CubeCache hit a non-quota IndexedDB read/write error
  | 'registry-evict' // CubeTableRegistry evicted an LRU table to stay within MAX_RESIDENT_TABLES

export interface CubeDiagEvent {
  /** epoch ms */
  at: number
  kind: CubeDiagEventKind
  /** `${scopeId}:${blastType}` when the event is tied to a specific cube */
  key?: string
  /** byte size involved (decompressed IPC bytes), where meaningful */
  bytes?: number
  /** row count, for `loaded` events */
  rows?: number
  /** free-text detail, e.g. 'fresh' | 'cache-304' | the error string */
  detail?: string
}

export interface CubeDiagCounters {
  loaded: number
  idbEvictions: number
  idbQuotaErrors: number
  idbErrors: number
  registryEvictions: number
}

const MAX_EVENTS = 50

const events: CubeDiagEvent[] = []
const counters: CubeDiagCounters = {
  loaded: 0,
  idbEvictions: 0,
  idbQuotaErrors: 0,
  idbErrors: 0,
  registryEvictions: 0
}
const listeners = new Set<() => void>()

const COUNTER_OF: Record<CubeDiagEventKind, keyof CubeDiagCounters> = {
  loaded: 'loaded',
  'idb-evict': 'idbEvictions',
  'idb-quota': 'idbQuotaErrors',
  'idb-error': 'idbErrors',
  'registry-evict': 'registryEvictions'
}

// Cap/quota events are worth a console signal even when the widget isn't mounted.
const NOISY_KINDS = new Set<CubeDiagEventKind>(['idb-evict', 'idb-quota', 'idb-error', 'registry-evict'])

export function recordCubeDiag(event: Omit<CubeDiagEvent, 'at'> & { at?: number }): void {
  const full: CubeDiagEvent = { at: event.at ?? Date.now(), ...event }
  events.push(full)
  if (events.length > MAX_EVENTS) events.splice(0, events.length - MAX_EVENTS)
  counters[COUNTER_OF[full.kind]] += 1

  if (NOISY_KINDS.has(full.kind)) {
    console.warn(`[viz.cube] ${full.kind}`, { key: full.key, bytes: full.bytes, detail: full.detail })
  }

  for (const cb of listeners) {
    try {
      cb()
    } catch {
      /* a broken subscriber must not break recording */
    }
  }
}

/** Most-recent-last snapshot of the event ring buffer. */
export function getCubeDiagEvents(): CubeDiagEvent[] {
  return [...events]
}

export function getCubeDiagCounters(): CubeDiagCounters {
  return { ...counters }
}

/** Subscribe to any diagnostics change. Returns an unsubscribe fn. */
export function subscribeCubeDiag(cb: () => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

/** Test/diagnostics reset. */
export function resetCubeDiag(): void {
  events.length = 0
  counters.loaded = 0
  counters.idbEvictions = 0
  counters.idbQuotaErrors = 0
  counters.idbErrors = 0
  counters.registryEvictions = 0
}
